import * as React from 'react';
import { connect } from 'react-redux';

import { Location } from '../../../common/react/Location';
import { encodeUriQueryExceptForSlashes } from '../../../ts/helpers/QueryParameterHelper';
import { VizPathInitialState } from '../../reducers/VizPathReducer';
import { selectVizPath } from '../../selectors/VizPathSelector';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '../../state/SpaceInfo';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedVizSpaceProvider, VizSpaceProviderOwnProps } from './VizSpaceProvider';

export namespace VizSpaceUrlUpdater {
  export interface OwnProps {
    readonly urls: SpacesSiteUrls;
    readonly vizPathFromUrl: string;
    readonly routeLocationSearch: string;
    readonly renderProvider: (props: ContainerProps) => JSX.Element;
  }

  interface StateProps {
    readonly vizPath: string;
  }

  export type ContainerProps = OwnProps & StateProps;

  export class Container extends React.PureComponent<ContainerProps> {
    public render() {
      return this.props.renderProvider(this.props);
    }

    public componentDidUpdate(prevProps: ContainerProps) {
      if (this.props.vizPath !== VizPathInitialState &&
        this.props.vizPath !== prevProps.vizPath &&
        this.props.vizPath !== this.props.vizPathFromUrl) {
        // vizPath changed via user clicking on viz tab or custom view dialog (that's why the url viz path does not match the
        // previous app state vizPath-- see tbViz), so update the url

        const prevVizPathSplit: string[] = prevProps.vizPath.split('/');
        const prevWorkbook    = prevVizPathSplit[0];
        const prevSheet       = prevVizPathSplit[1];
        const prevCustomView  = prevVizPathSplit.length > 2 ? prevVizPathSplit[2] + '/' + prevVizPathSplit[3] : undefined;

        const newVizPathSplit: string[] = this.props.vizPath.split('/');
        const newWorkbook     = newVizPathSplit[0];
        const newSheet        = newVizPathSplit[1];
        const newCustomView   = newVizPathSplit.length > 2 ? newVizPathSplit[2] + '/' + newVizPathSplit[3] : undefined;

        // vizclient returns an encoded vizpath when the url is not encoded, we want to use the encoded the url but 
        // just replace it.
        const sameVizPath_JustEncoded = encodeUriQueryExceptForSlashes(this.props.vizPathFromUrl) === this.props.vizPath;

        // Use a full viewUrl so that we don't cut off any part of the vizPath (since a viz path can be made up of
        // many parts like "/workbookName/viewName/userName@domainName/customViewName").
        // routeLocationSearch already includes the ? so just add it to keep the search parameters
        // we want to encode some of the path but not the slashes
        const newUrl = this.props.urls.viewUrl(encodeUriQueryExceptForSlashes(this.props.vizPath) + this.props.routeLocationSearch);

        const differentSheet_SameWorkbook    = newSheet !== prevSheet && newWorkbook === prevWorkbook;
        const sameSheet_RedirectToCustomView = newSheet === prevSheet && newWorkbook === prevWorkbook &&
                                               prevCustomView === undefined && newCustomView !== undefined;
        if (differentSheet_SameWorkbook || sameSheet_RedirectToCustomView || sameVizPath_JustEncoded) {
          // if just changing tabs in the same workbook or just redirecting to a custom view, don't add to history
          Location.instance.replace(newUrl);
        } else {
          // otherwise, change url AND add to browser history
          Location.instance.setUrl(newUrl);
        }
      }
    }
  }

  const mapStateToProps: MapStateToProps<StateProps> = (appState) => ({
    vizPath: selectVizPath(appState),
  });

  export const ConnectedContainer: React.ComponentClass<OwnProps>
    = connect(mapStateToProps)(Container);

  export const renderProvider = (props: ContainerProps) => {
    const renderVizSpaceProps: VizSpaceProviderOwnProps = {
      vizPathFromUrl: props.vizPathFromUrl,
      routeLocationSearch: props.routeLocationSearch,
      urls: props.urls,
    };

    const spaceInfo: SpaceInfo = {
      spaceType: 'view',
      subspaceName: undefined,
      item: SpaceItemForNonEntitySpace  // this is fetched in setupScope in VizSpaceProvider
    };

    return (
      <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
        <ConnectedVizSpaceProvider {...renderVizSpaceProps} />
      </ConnectedSpaceInfoProvider>);
  };
}
